import React, { useState } from 'react';
import { Button, Chip, IconButton, Menu, MenuItem, Skeleton, SearchField, StatusPill, fieldStyle, labelStyle } from './controls';
import { ActionSheet, Overlay } from './Dialog';
import { Icon, IconName } from './icons';
import { Translate } from './i18n';
import { CANVAS, CARD_BORDER, DIM, DIVIDER, FAMILY, Family, INK, MONO, MUTED, StatusInfo, TEAL, TEXT, TINT, card } from './tokens';

/* ---------------- Page title ---------------- */

export interface TitleAction {
  key: string;
  label: string;
  icon?: IconName;
  onSelect: () => void;
}

/** H1 + meta line; secondary actions then one primary. Phone: secondaries fold into "More actions", the primary goes full width. */
export const PageTitle: React.FC<{ phone: boolean; t: Translate; title: string; meta?: string; secondary?: TitleAction[]; primary?: TitleAction }> = ({
  phone,
  t,
  title,
  meta,
  secondary = [],
  primary,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  if (phone) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <h1 style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>{title}</h1>
            {meta && <p style={{ marginTop: '2px', fontSize: '13px', lineHeight: '18px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{meta}</p>}
          </div>
          {secondary.length > 0 && (
            <>
              <IconButton icon="moreHorizontal" label={t('More actions')} phone bordered aria-haspopup="dialog" onClick={() => setSheetOpen(true)} />
              <ActionSheet open={sheetOpen} title={t('More actions')} closeLabel={t('Close')} items={secondary} onClose={() => setSheetOpen(false)} />
            </>
          )}
        </div>
        {primary && (
          <Button variant="primary" phone fullWidth icon={primary.icon} onClick={primary.onSelect}>
            {primary.label}
          </Button>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <h1 style={{ fontSize: '24px', lineHeight: '32px', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>{title}</h1>
        {meta && <p style={{ marginTop: '4px', fontSize: '14px', lineHeight: '20px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{meta}</p>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        {secondary.map((action) => (
          <Button key={action.key} icon={action.icon} onClick={action.onSelect}>
            {action.label}
          </Button>
        ))}
        {primary && (
          <Button variant="primary" icon={primary.icon} onClick={primary.onSelect}>
            {primary.label}
          </Button>
        )}
      </div>
    </div>
  );
};

/* ---------------- Toolbar ---------------- */

export type PeriodKey = 'today' | '7d' | '30d' | 'custom';

export const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'custom', label: 'Custom' },
];

export interface FilterDef {
  key: string;
  label: string;
  icon: IconName;
  allLabel: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (value: string) => void;
}

const Segments: React.FC<{ phone: boolean; label: string; t: Translate; value: PeriodKey; onChange: (value: PeriodKey) => void }> = ({ phone, label, t, value, onChange }) => (
  <div role="group" aria-label={label} style={{ display: 'flex', gap: '4px', padding: '4px', background: CANVAS, borderRadius: '12px' }}>
    {PERIOD_OPTIONS.map((option) => {
      const pressed = option.key === value;
      return (
        <button
          key={option.key}
          type="button"
          className="pu-seg"
          aria-pressed={pressed}
          onClick={() => onChange(option.key)}
          style={{
            flex: phone ? '1 1 0' : undefined,
            minWidth: phone ? 0 : undefined,
            minHeight: phone ? '44px' : undefined,
            padding: phone ? '8px 4px' : '8px 14px',
            border: 0,
            borderRadius: '8px',
            background: pressed ? '#FFFFFF' : 'transparent',
            boxShadow: pressed ? '0 1px 3px rgba(5,41,42,0.08)' : undefined,
            color: pressed ? INK : MUTED,
            fontSize: '13px',
            lineHeight: '18px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          {t(option.label)}
        </button>
      );
    })}
  </div>
);

const FilterPill: React.FC<{ filter: FilterDef; t: Translate }> = ({ filter, t }) => (
  <label className="pu-filter" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: CANVAS, border: `1px solid ${CARD_BORDER}`, borderRadius: '12px', cursor: 'pointer' }}>
    <Icon name={filter.icon} color={MUTED} />
    <span style={{ fontSize: '12.5px', lineHeight: '18px', fontWeight: 500, color: MUTED }}>{t(filter.label)}</span>
    <select
      value={filter.value}
      onChange={(event) => filter.onChange(event.target.value)}
      style={{ minWidth: '120px', maxWidth: '220px', padding: '0 20px 0 0', border: 0, background: 'transparent', color: INK, fontSize: '13px', lineHeight: '18px', fontWeight: 500, appearance: 'none', cursor: 'pointer', textOverflow: 'ellipsis' }}
    >
      <option value="">{t(filter.allLabel)}</option>
      {filter.options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
    <Icon name="chevronDown" size={14} color={MUTED} style={{ marginLeft: '-22px', pointerEvents: 'none' }} />
  </label>
);

const DateField: React.FC<{ phone: boolean; label: string; value: string; min?: string; max?: string; onChange: (value: string) => void }> = ({ phone, label, value, min, max, onChange }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: phone ? '1 1 0' : undefined, minWidth: 0 }}>
    <span style={labelStyle}>{label}</span>
    <input className="pu-input" type="date" value={value} min={min} max={max} onChange={(event) => onChange(event.target.value)} style={{ ...fieldStyle(phone), width: phone ? '100%' : '160px' }} />
  </label>
);

export const ToolbarCard: React.FC<{
  phone: boolean;
  t: Translate;
  period: PeriodKey;
  onPeriod: (value: PeriodKey) => void;
  customFrom: string;
  customTo: string;
  onCustom: (from: string, to: string) => void;
  search: string;
  onSearch: (value: string) => void;
  searchLabel: string;
  searchPlaceholder: string;
  filters: FilterDef[];
  onOpenFilters: () => void;
}> = ({ phone, t, period, onPeriod, customFrom, customTo, onCustom, search, onSearch, searchLabel, searchPlaceholder, filters, onOpenFilters }) => {
  const active = filters.filter((filter) => filter.value).length;
  const custom = period === 'custom' && (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: phone ? '8px' : '12px' }}>
      <DateField phone={phone} label={t('From')} value={customFrom} max={customTo || undefined} onChange={(value) => onCustom(value, customTo)} />
      <DateField phone={phone} label={t('To')} value={customTo} min={customFrom || undefined} onChange={(value) => onCustom(customFrom, value)} />
    </div>
  );
  if (phone) {
    return (
      <section aria-label={t('Filters')} style={{ ...card, display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px' }}>
        <Segments phone label={t('Period')} t={t} value={period} onChange={onPeriod} />
        {custom}
        <div style={{ display: 'flex', gap: '8px' }}>
          <SearchField phone label={searchLabel} placeholder={searchPlaceholder} value={search} onChange={onSearch} />
          {filters.length > 0 && (
            <button
              type="button"
              className="pu-btn-secondary"
              aria-haspopup="dialog"
              aria-label={active ? t('Filters, {count} active', { count: active }) : t('Filters')}
              onClick={onOpenFilters}
              style={{ height: '44px', flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0 12px', border: '1px solid #C9D7D8', borderRadius: '12px', background: '#FFFFFF', color: TEXT, fontSize: '15px', lineHeight: '20px', fontWeight: 500, cursor: 'pointer' }}
            >
              <Icon name="sliders" size={18} color={MUTED} />
              {t('Filters')}
              {active > 0 && (
                <span style={{ minWidth: '20px', height: '20px', padding: '0 6px', borderRadius: '999px', background: TEAL, color: '#FFFFFF', fontSize: '12px', lineHeight: '20px', fontWeight: 600, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{active}</span>
              )}
            </button>
          )}
        </div>
      </section>
    );
  }
  return (
    <section aria-label={t('Filters')} style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', padding: '12px' }}>
      <Segments phone={false} label={t('Period')} t={t} value={period} onChange={onPeriod} />
      {custom}
      <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        {/* A set width, so the row measures the box at 300px instead of the input's own width. */}
        <div style={{ flex: '0 1 300px', width: '300px', minWidth: '260px', display: 'flex' }}>
          <SearchField label={searchLabel} placeholder={searchPlaceholder} value={search} onChange={onSearch} />
        </div>
        {filters.map((filter) => (
          <FilterPill key={filter.key} filter={filter} t={t} />
        ))}
      </div>
    </section>
  );
};

/** Phone filter sheet: chips apply at once; the button closes the sheet on the matching rows. */
export const FiltersSheet: React.FC<{ open: boolean; t: Translate; filters: FilterDef[]; showLabel: string; onClear: () => void; onClose: () => void }> = ({
  open,
  t,
  filters,
  showLabel,
  onClear,
  onClose,
}) => {
  const active = filters.filter((filter) => filter.value).length;
  return (
    <Overlay
      open={open}
      phone
      title={t('Filters')}
      titleId="pu-filters-title"
      closeLabel={t('Close filters')}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <p aria-live="polite" style={{ fontSize: '14px', lineHeight: '20px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
              {active === 1 ? t('1 filter') : t('{count} filters', { count: active })}
            </p>
            <Button variant="text" phone disabled={!active} onClick={onClear}>
              {t('Clear all')}
            </Button>
          </div>
          <Button variant="primary" phone fullWidth onClick={onClose}>
            {showLabel}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {filters.map((filter) => (
          <div key={filter.key} role="group" aria-labelledby={`pu-fg-${filter.key}`} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h3 id={`pu-fg-${filter.key}`} style={{ fontSize: '15px', lineHeight: '22px', fontWeight: 600, color: INK }}>
              {t(filter.label)}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Chip phone pressed={!filter.value} onClick={() => filter.onChange('')}>
                {t(filter.allLabel)}
              </Chip>
              {filter.options.map((option) => (
                <Chip key={option.id} phone pressed={filter.value === option.id} onClick={() => filter.onChange(option.id)}>
                  {option.name}
                </Chip>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Overlay>
  );
};

/* ---------------- Status strip ---------------- */

export interface StripItem {
  key: string;
  label: string;
  family: Family | null;
  count: number;
}

export const StatusStrip: React.FC<{ phone: boolean; label: string; items: StripItem[]; active: string; onPick: (key: string) => void }> = ({ phone, label, items, active, onPick }) => (
  <section
    aria-label={label}
    style={
      phone
        ? { ...card, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '4px', padding: '8px' }
        : { ...card, display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '8px' }
    }
  >
    {items.map((item) => {
      const pressed = item.key === active;
      return (
        <button
          key={item.key}
          type="button"
          className="pu-strip-btn"
          aria-pressed={pressed}
          onClick={() => onPick(item.key)}
          style={{
            flex: phone ? undefined : '1 1 auto',
            minWidth: phone ? 0 : '120px',
            minHeight: phone ? '44px' : undefined,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            padding: '10px 12px',
            border: `1px solid ${pressed ? TEAL : 'transparent'}`,
            borderRadius: '12px',
            background: pressed ? '#F3FAFA' : 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: MUTED, whiteSpace: phone ? undefined : 'nowrap' }}>
            {item.family && <span aria-hidden="true" style={{ width: '8px', height: '8px', flex: 'none', borderRadius: '999px', background: FAMILY[item.family].fg }} />}
            {item.label}
          </span>
          <span style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums' }}>{item.count}</span>
        </button>
      );
    })}
  </section>
);

/* ---------------- Document table (desktop) and rows (phone) ---------------- */

export interface DocColumn {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /** Sized to its content, 24px after the column before it. */
  compact?: boolean;
}

export interface DocCell {
  main: React.ReactNode;
  /** Muted second line: the site, the person, the destination. */
  sub?: string | null;
  strong?: boolean;
}

/** One order, delivery, transfer or correction in a list. */
export interface DocListRow {
  id: string;
  number: string;
  /** Under the number: the template, the reason or the comment. */
  subtitle?: string | null;
  cells: Record<string, DocCell>;
  status: StatusInfo;
  /** Under the status pill, e.g. "3 of 4 lines received". */
  statusNote?: string | null;
  /** Phone card: line 2, then line 3 ending in the bold amount. */
  phoneText: string;
  phoneMeta: string;
  phoneAmount?: string;
}

const head = (align: 'left' | 'right', compact: boolean, first = false): React.CSSProperties => ({
  width: compact ? '1%' : undefined,
  height: '44px',
  padding: first ? '0 6px 0 16px' : compact ? '0 6px 0 24px' : '0 6px',
  background: TINT,
  borderBottom: `1px solid ${DIVIDER}`,
  textAlign: align,
  fontSize: '12.5px',
  lineHeight: '18px',
  fontWeight: 600,
  color: MUTED,
  whiteSpace: 'nowrap',
});

const cell = (compact: boolean, first = false): React.CSSProperties => ({
  height: '52px',
  padding: first ? '0 6px 0 16px' : compact ? '0 6px 0 24px' : '0 6px',
  borderBottom: `1px solid ${DIVIDER}`,
  verticalAlign: 'middle',
});

const second: React.CSSProperties = { display: 'block', fontSize: '12.5px', lineHeight: '18px', color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const openOnClick = (onOpen: () => void) => (event: React.MouseEvent<HTMLAnchorElement>) => {
  // Modified clicks keep the browser's own behaviour (new tab, new window).
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  onOpen();
};

export const DocumentTable: React.FC<{
  t: Translate;
  label: string;
  /** Header of the number column: Order, Transfer, Correction. */
  numberLabel: string;
  columns: DocColumn[];
  rows: DocListRow[];
  loading: boolean;
  hrefOf: (row: DocListRow) => string;
  onOpen: (row: DocListRow) => void;
  menuFor: (row: DocListRow) => MenuItem[];
  /** Names of the row menu and its button, e.g. "Actions for order #1042". */
  menuLabel: (row: DocListRow) => string;
  triggerLabel: (row: DocListRow) => string;
  empty: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ t, label, numberLabel, columns, rows, loading, hrefOf, onOpen, menuFor, menuLabel, triggerLabel, empty, footer }) => (
  <section aria-label={label} aria-busy={loading || undefined} style={{ ...card, overflow: 'hidden' }}>
    {!loading && rows.length === 0 ? (
      <div style={{ padding: '16px' }}>{empty}</div>
    ) : (
      <div style={{ position: 'relative', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px', lineHeight: '20px', color: TEXT }}>
          <thead>
            <tr>
              <th scope="col" style={head('left', false, true)}>
                {numberLabel}
              </th>
              {columns.map((column) => (
                <th key={column.key} scope="col" style={head(column.align ?? 'left', !!column.compact)}>
                  {column.label}
                </th>
              ))}
              <th scope="col" style={head('left', true)}>
                {t('Status')}
              </th>
              <th scope="col" style={{ ...head('left', false), width: '56px', padding: '0 16px 0 6px' }}>
                <span className="pu-sr">{t('Actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? [0, 1, 2, 3, 4].map((index) => (
                  <tr key={`loading-${index}`}>
                    <td style={cell(false, true)}>
                      <Skeleton width="72px" />
                    </td>
                    {columns.map((column) => (
                      <td key={column.key} style={cell(!!column.compact)}>
                        <Skeleton width={column.compact ? '64px' : '60%'} />
                      </td>
                    ))}
                    <td style={cell(true)}>
                      <Skeleton width="96px" height={20} />
                    </td>
                    <td style={{ ...cell(false), padding: '0 16px 0 6px' }} />
                  </tr>
                ))
              : rows.map((row) => (
                  <tr key={row.id} className="pu-row">
                    <td style={cell(false, true)}>
                      <a
                        href={hrefOf(row)}
                        className="pu-link"
                        onClick={openOnClick(() => onOpen(row))}
                        style={{ display: 'block', fontFamily: MONO, fontSize: '14px', lineHeight: '20px', fontWeight: 600, color: INK, textDecoration: 'none' }}
                      >
                        {row.number}
                      </a>
                      {row.subtitle && <span style={second}>{row.subtitle}</span>}
                    </td>
                    {columns.map((column) => {
                      const value = row.cells[column.key];
                      const right = column.align === 'right';
                      return (
                        <td
                          key={column.key}
                          style={{
                            ...cell(!!column.compact),
                            textAlign: right ? 'right' : undefined,
                            whiteSpace: column.compact ? 'nowrap' : undefined,
                            fontVariantNumeric: right ? 'tabular-nums' : undefined,
                          }}
                        >
                          <span style={{ display: 'block', fontWeight: value?.strong ? 500 : undefined, color: value?.strong ? INK : undefined }}>{value?.main ?? '—'}</span>
                          {value?.sub && <span style={second}>{value.sub}</span>}
                        </td>
                      );
                    })}
                    <td style={cell(true)}>
                      <StatusPill status={row.status} t={t} />
                      {row.statusNote && <span style={{ ...second, marginTop: '2px', fontVariantNumeric: 'tabular-nums' }}>{row.statusNote}</span>}
                    </td>
                    <td style={{ ...cell(false), padding: '0 16px 0 6px', textAlign: 'right' }}>
                      <Menu label={menuLabel(row)} items={menuFor(row)} width={220} trigger={(props) => <IconButton icon="moreVertical" label={triggerLabel(row)} {...props} />} />
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    )}
    {footer}
  </section>
);

export const PhoneDocumentList: React.FC<{
  t: Translate;
  label: string;
  rows: DocListRow[];
  loading: boolean;
  hrefOf: (row: DocListRow) => string;
  onOpen: (row: DocListRow) => void;
  empty: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ t, label, rows, loading, hrefOf, onOpen, empty, footer }) => (
  <section aria-label={label} aria-busy={loading || undefined} style={{ ...card, overflow: 'hidden' }}>
    {!loading && rows.length === 0 ? (
      <div style={{ padding: '16px' }}>{empty}</div>
    ) : (
      <ul>
        {loading
          ? [0, 1, 2, 3].map((index) => (
              <li key={`loading-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', borderBottom: `1px solid ${DIVIDER}` }}>
                <Skeleton width="40%" />
                <Skeleton width="70%" />
                <Skeleton width="50%" />
              </li>
            ))
          : rows.map((row) => (
              <li key={row.id} style={{ borderBottom: `1px solid ${DIVIDER}` }}>
                <a
                  href={hrefOf(row)}
                  className="pu-inset"
                  onClick={openOnClick(() => onOpen(row))}
                  style={{ minHeight: '64px', display: 'flex', flexDirection: 'column', gap: '4px', padding: '14px 16px', color: TEXT, textDecoration: 'none' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <span style={{ fontFamily: MONO, fontSize: '15px', lineHeight: '22px', fontWeight: 600, color: INK }}>{row.number}</span>
                    <StatusPill status={row.status} t={t} />
                  </span>
                  <span style={{ fontSize: '15px', lineHeight: '22px', color: TEXT }}>{row.phoneText}</span>
                  <span style={{ fontSize: '13px', lineHeight: '18px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                    {row.phoneMeta}
                    {row.phoneAmount && (
                      <>
                        {row.phoneMeta ? ' · ' : ''}
                        <span style={{ fontWeight: 600, color: INK }}>{row.phoneAmount}</span>
                      </>
                    )}
                  </span>
                </a>
              </li>
            ))}
      </ul>
    )}
    {footer}
  </section>
);

/* ---------------- Pager ---------------- */

/** Page numbers with gaps: first, last and the two around the current page. */
const pagesFor = (page: number, count: number): (number | 'gap')[] => {
  const wanted = new Set([1, count, page - 1, page, page + 1].filter((n) => n >= 1 && n <= count));
  const sorted = [...wanted].sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  sorted.forEach((n, index) => {
    if (index > 0 && n - sorted[index - 1] > 1) out.push('gap');
    out.push(n);
  });
  return out;
};

export const Pager: React.FC<{ phone: boolean; t: Translate; page: number; limit: number; total: number; onPage: (page: number) => void }> = ({ phone, t, page, limit, total, onPage }) => {
  const count = Math.max(1, Math.ceil(total / limit));
  if (total <= limit && page === 1) return null;
  const first = (page - 1) * limit + 1;
  const last = Math.min(total, page * limit);
  const arrow = (direction: 'prev' | 'next') => (
    <button
      type="button"
      className="pu-btn-icon"
      aria-label={direction === 'prev' ? t('Previous page') : t('Next page')}
      disabled={direction === 'prev' ? page <= 1 : page >= count}
      onClick={() => onPage(direction === 'prev' ? page - 1 : page + 1)}
      style={{
        width: phone ? '44px' : '32px',
        height: phone ? '44px' : '32px',
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: phone ? '1px solid #C9D7D8' : 0,
        borderRadius: '999px',
        background: phone ? '#FFFFFF' : 'transparent',
        color: MUTED,
        cursor: 'pointer',
      }}
    >
      <Icon name={direction === 'prev' ? 'chevronLeft' : 'chevronRight'} size={phone ? 18 : 16} />
    </button>
  );
  if (phone) {
    return (
      <nav aria-label={t('Pages')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 16px' }}>
        {arrow('prev')}
        <p style={{ fontSize: '13px', lineHeight: '18px', color: DIM, fontVariantNumeric: 'tabular-nums' }}>{t('Page {page} of {count}', { page, count })}</p>
        {arrow('next')}
      </nav>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 16px' }}>
      <p style={{ fontSize: '13px', lineHeight: '18px', color: DIM, fontVariantNumeric: 'tabular-nums' }}>{t('{first}–{last} of {total}', { first, last, total })}</p>
      <nav aria-label={t('Pages')} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {arrow('prev')}
        {pagesFor(page, count).map((entry, index) =>
          entry === 'gap' ? (
            <span key={`gap-${index}`} aria-hidden="true" style={{ minWidth: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: MUTED }}>
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={entry === page ? undefined : 'pu-btn-secondary'}
              aria-current={entry === page ? 'page' : undefined}
              aria-label={t('Page {page}', { page: entry })}
              onClick={() => onPage(entry)}
              style={{
                minWidth: '32px',
                height: '32px',
                padding: '0 8px',
                border: 0,
                borderRadius: '999px',
                background: entry === page ? TEAL : 'transparent',
                color: entry === page ? '#FFFFFF' : MUTED,
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                cursor: 'pointer',
              }}
            >
              {entry}
            </button>
          ),
        )}
        {arrow('next')}
      </nav>
    </div>
  );
};

/* ---------------- Summary panel ---------------- */

export interface ShareRow {
  key: string;
  name: string;
  value: string;
  detail: string;
  /** 0..100 */
  share: number;
}

/** "By supplier": a list of shares with a bar each; capped at 320px beside the table (under it below 1360px). */
export const SharePanel: React.FC<{ label: string; title: string; subtitle?: string; rows: ShareRow[]; empty: string }> = ({ label, title, subtitle, rows, empty }) => (
  <section aria-label={label} style={{ ...card, padding: '20px' }}>
    <h2 style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: INK }}>{title}</h2>
    {subtitle && <p style={{ marginTop: '2px', fontSize: '12.5px', lineHeight: '18px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{subtitle}</p>}
    {rows.length === 0 ? (
      <p style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${DIVIDER}`, fontSize: '13px', lineHeight: '18px', color: MUTED }}>{empty}</p>
    ) : (
      <ul style={{ marginTop: '12px' }}>
        {rows.map((row, index) => (
          <li key={row.key} style={{ padding: index === rows.length - 1 ? '12px 0 0' : '12px 0', borderTop: `1px solid ${DIVIDER}` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
              <span style={{ minWidth: 0, fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: TEXT, overflowWrap: 'anywhere' }}>{row.name}</span>
              <span style={{ flex: 'none', fontSize: '14px', lineHeight: '20px', fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums' }}>{row.value}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <span aria-hidden="true" style={{ flex: '1 1 auto', height: '6px', borderRadius: '999px', background: DIVIDER, overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${Math.max(0, Math.min(100, row.share))}%`, height: '6px', borderRadius: '999px', background: TEAL }} />
              </span>
              <span style={{ flex: 'none', fontSize: '12.5px', lineHeight: '18px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{row.detail}</span>
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

